import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/utils/supabase";
import { Rider } from "@/utils/types";
import { Loader } from "@/components/loader";
import { useAdmin } from "@/hooks/use-admin";
import { ConfirmModal } from "@/components/confirm-modal";
import { MemberNotesModal } from "@/components";
import toast from "react-hot-toast";
import { TrashIcon } from "@heroicons/react/24/solid";

type RiderWithMembership = Rider & {
  membershipNumber?: string | null;
};

export default function MembersPage() {
  const [members, setMembers] = useState<RiderWithMembership[]>([]);
  const [showActivatedOnly, setShowActivatedOnly] = useState(true);
  const [membershipSort, setMembershipSort] = useState<"none" | "asc" | "desc">(
    "asc"
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin, loading: adminLoading } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      toast.error("Access denied. Admin privileges required.");
      navigate("/dashboard/profile");
    }
  }, [isAdmin, adminLoading, navigate]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Rider | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [updatingActivated, setUpdatingActivated] = useState<string | null>(
    null
  );
  const [editingMembership, setEditingMembership] = useState<string | null>(
    null
  );
  const [membershipDrafts, setMembershipDrafts] = useState<
    Record<string, string>
  >({});
  const [savingMembership, setSavingMembership] = useState<string | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [memberToEditNotes, setMemberToEditNotes] =
    useState<RiderWithMembership | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    async function fetchMembers() {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .schema("private")
          .from("riders")
          .select("*")
          .order("firstName", { ascending: true });

        if (error) {
          setError(error.message);
          console.error("Error fetching members:", error);
        } else {
          setMembers(data || []);
        }
      } catch (err: any) {
        setError("An unexpected error occurred");
        console.error("Unexpected error:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchMembers();
  }, []);

  const displayedMembers = useMemo(() => {
    let nextMembers = [...members];

    if (showActivatedOnly) {
      nextMembers = nextMembers.filter((member) => member.isActivated === true);
    }

    if (membershipSort === "none") {
      return nextMembers;
    }

    const parseMembership = (value?: string | null) => {
      if (!value) return null;

      const trimmed = value.trim();
      if (!trimmed) return null;

      const asNumber = Number(trimmed);
      if (!Number.isNaN(asNumber)) {
        return asNumber;
      }

      return trimmed.toLowerCase();
    };

    return nextMembers.sort((a, b) => {
      const aValue = parseMembership(a.membershipNumber);
      const bValue = parseMembership(b.membershipNumber);

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return membershipSort === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aText = String(aValue);
      const bText = String(bValue);
      const result = aText.localeCompare(bText);
      return membershipSort === "asc" ? result : -result;
    });
  }, [members, showActivatedOnly, membershipSort]);

  const handleToggleActivated = async (member: Rider) => {
    if (!isAdmin) {
      toast.error("You don't have permission to perform this action");
      return;
    }

    setUpdatingActivated(member.uuid);
    try {
      const newValue = !member.isActivated;

      const { data, error } = await supabase
        .schema("private")
        .from("riders")
        .update({ isActivated: newValue })
        .eq("uuid", member.uuid)
        .select();

      if (error) {
        toast.error(error.message || "Failed to update activation status");
        console.error("Error updating activation status:", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
      } else {
        // Verify the update worked
        if (data && data.length > 0) {
          const updatedMember = data[0];
          // Update local state with the actual data from DB
          setMembers((prev) =>
            prev.map((m) =>
              m.uuid === member.uuid
                ? { ...m, isActivated: updatedMember.isActivated }
                : m
            )
          );
          toast.success(
            `Account ${updatedMember.isActivated ? "activated" : "deactivated"}`
          );
        } else {
          // No data returned, but no error - might be RLS issue
          console.warn(
            "Update succeeded but no data returned. Possible RLS policy issue."
          );
          toast.error(
            "Update may have failed. Please check Row Level Security policies."
          );
        }
      }
    } catch (err: any) {
      toast.error("An unexpected error occurred");
      console.error("Unexpected error:", err);
    } finally {
      setUpdatingActivated(null);
    }
  };

  const handleSaveMembershipNumber = async (member: RiderWithMembership) => {
    if (!isAdmin) {
      toast.error("You don't have permission to perform this action");
      return;
    }

    const membershipNumber = (membershipDrafts[member.uuid] ?? "").trim();

    setSavingMembership(member.uuid);
    try {
      const { error } = await supabase
        .schema("private")
        .from("riders")
        .update({ membershipNumber })
        .eq("uuid", member.uuid);

      if (error) {
        toast.error(error.message || "Failed to update membership number");
        console.error("Error updating membership number:", error);
        return;
      }

      setMembers((prev) =>
        prev.map((m) =>
          m.uuid === member.uuid ? { ...m, membershipNumber } : m
        )
      );
      setEditingMembership(null);
      toast.success("Membership number updated");
    } catch (err: any) {
      toast.error("An unexpected error occurred");
      console.error("Unexpected error:", err);
    } finally {
      setSavingMembership(null);
    }
  };

  const handleDeleteClick = (member: Rider) => {
    if (!isAdmin) {
      toast.error("You don't have permission to perform this action");
      return;
    }
    setMemberToDelete(member);
    setDeleteModalOpen(true);
  };

  const handleOpenNotesModal = (member: RiderWithMembership) => {
    if (!isAdmin) {
      toast.error("You don't have permission to perform this action");
      return;
    }

    setMemberToEditNotes(member);
    setNotesDraft(member.adminNotes ?? "");
    setNotesModalOpen(true);
  };

  const handleCloseNotesModal = () => {
    if (savingNotes) return;
    setNotesModalOpen(false);
    setMemberToEditNotes(null);
    setNotesDraft("");
  };

  const handleSaveNotes = async () => {
    if (!isAdmin || !memberToEditNotes) {
      toast.error("You don't have permission to perform this action");
      return;
    }

    setSavingNotes(true);
    try {
      const adminNotes = notesDraft.trim();
      const valueToSave = adminNotes.length > 0 ? adminNotes : null;

      const { error } = await supabase
        .schema("private")
        .from("riders")
        .update({ adminNotes: valueToSave })
        .eq("uuid", memberToEditNotes.uuid);

      if (error) {
        toast.error(error.message || "Failed to update notes");
        console.error("Error updating notes:", error);
        return;
      }

      setMembers((prev) =>
        prev.map((m) =>
          m.uuid === memberToEditNotes.uuid
            ? { ...m, adminNotes: valueToSave }
            : m
        )
      );

      toast.success("Notes updated");
      setNotesModalOpen(false);
      setMemberToEditNotes(null);
      setNotesDraft("");
    } catch (err: any) {
      toast.error("An unexpected error occurred");
      console.error("Unexpected error:", err);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete || !isAdmin) return;

    setDeleting(true);
    try {
      // Delete from riders table first
      const { error: riderError } = await supabase
        .from("riders")
        .delete()
        .eq("uuid", memberToDelete.uuid);

      if (riderError) {
        toast.error(
          riderError.message || "Failed to delete member from riders table"
        );
        console.error("Error deleting rider:", riderError);
        setDeleting(false);
        return;
      }

      // Delete user from auth.users using RPC function
      const { error: authError } = await supabase.rpc("admin_delete_user", {
        target_uuid: memberToDelete.uuid,
      });

      if (authError) {
        toast.error(authError.message || "Failed to delete user from auth");
        console.error("Error deleting user from auth:", authError);
        // Note: Rider is already deleted, but auth user deletion failed
        // Still update UI since rider is gone
      }

      // Remove from local state
      setMembers((prev) => prev.filter((m) => m.uuid !== memberToDelete.uuid));
      toast.success("Member deleted successfully");
      setDeleteModalOpen(false);
      setMemberToDelete(null);
    } catch (err: any) {
      toast.error("An unexpected error occurred");
      console.error("Unexpected error:", err);
    } finally {
      setDeleting(false);
    }
  };

  if (adminLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-red-500">Error loading members: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Members</h1>
        <p className="text-gray-600 mt-2">View and manage all team members</p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showActivatedOnly}
              onChange={(e) => setShowActivatedOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Activated users only
          </label>

          <div className="inline-flex items-center gap-2 text-sm text-gray-700">
            <span>Sort membership number:</span>
            <select
              value={membershipSort}
              onChange={(e) =>
                setMembershipSort(e.target.value as "none" | "asc" | "desc")
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
              <option value="none">No sorting</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Membership
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Confirmed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayedMembers.length === 0 ? (
                <tr>
                  <td
                    colSpan={isAdmin ? 10 : 9}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No members found for the current filter
                  </td>
                </tr>
              ) : (
                displayedMembers.map((member) => {
                  // Get email from auth.users table if needed
                  // For now, we'll show what we have from riders table
                  const fullName = `${member.firstName || ""} ${
                    member.lastName || ""
                  }`.trim();

                  return (
                    <tr key={member.uuid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {member.avatarUrl ? (
                            <img
                              className="h-10 w-10 rounded-full mr-3"
                              src={member.avatarUrl}
                              alt={fullName || "Member"}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold mr-3">
                              {(member.firstName?.[0] || "").toUpperCase()}
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900">
                            {fullName || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {member.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isAdmin && editingMembership === member.uuid ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={
                                membershipDrafts[member.uuid] ??
                                member.membershipNumber ??
                                ""
                              }
                              onChange={(e) =>
                                setMembershipDrafts((prev) => ({
                                  ...prev,
                                  [member.uuid]: e.target.value,
                                }))
                              }
                              className="w-32 rounded border border-gray-300 px-2 py-1 text-sm"
                              placeholder="Membership No."
                            />
                            <button
                              onClick={() => handleSaveMembershipNumber(member)}
                              disabled={savingMembership === member.uuid}
                              className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              {savingMembership === member.uuid
                                ? "..."
                                : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingMembership(null)}
                              disabled={savingMembership === member.uuid}
                              className="rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingMembership(member.uuid);
                              setMembershipDrafts((prev) => ({
                                ...prev,
                                [member.uuid]: member.membershipNumber ?? "",
                              }));
                            }}
                            className="text-left text-sm text-gray-700 hover:text-blue-600"
                            title="Click to edit membership number"
                          >
                            {member.membershipNumber || "-"}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.registrationFormUrl ? (
                          <a
                            href={member.registrationFormUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
                          >
                            📄 View
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            member.isEmailConfirmed
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {member.isEmailConfirmed ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isAdmin ? (
                          <button
                            onClick={() => handleToggleActivated(member)}
                            disabled={updatingActivated === member.uuid}
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer transition-colors ${
                              member.isActivated
                                ? "bg-green-100 text-green-800 hover:bg-green-200"
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            title="Click to toggle activation status"
                          >
                            {updatingActivated === member.uuid ? (
                              <Loader />
                            ) : member.isActivated ? (
                              "Yes"
                            ) : (
                              "No"
                            )}
                          </button>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              member.isActivated
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {member.isActivated ? "Yes" : "No"}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleOpenNotesModal(member)}
                          className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                          title="Change admin notes"
                        >
                          {(member.adminNotes ?? "").trim().length > 0
                            ? "Edit"
                            : "Change"}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            member.is_admin
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {member.is_admin ? "Yes" : "No"}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.updateAt
                          ? new Date(member.updateAt).toLocaleDateString()
                          : "-"}
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDeleteClick(member)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="Delete member"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Showing {displayedMembers.length} of {members.length} members
      </div>

      <MemberNotesModal
        isOpen={notesModalOpen}
        member={memberToEditNotes}
        notes={notesDraft}
        loading={savingNotes}
        onChangeNotes={setNotesDraft}
        onClose={handleCloseNotesModal}
        onSave={handleSaveNotes}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setMemberToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Member"
        message={
          memberToDelete
            ? `Are you sure you want to delete ${memberToDelete.firstName || ""} ${memberToDelete.lastName || ""} (${memberToDelete.email})? This action cannot be undone.`
            : "Are you sure you want to delete this member?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="red"
        loading={deleting}
      />
    </div>
  );
}
